import { useEffect, useId, useRef, useState } from "react";

interface InfoTooltipProps {
  /** Accessible label for the trigger button (read by screen readers). */
  label: string;
  /** Tooltip body — already-rendered JSX so callers can include links. */
  children: React.ReactNode;
}

/**
 * Small "ⓘ" trigger that reveals a tooltip. Behavior:
 *   - Desktop hover → tooltip appears.
 *   - Mobile/touch tap → tooltip toggles.
 *   - Esc or outside-click → tooltip closes.
 *   - Keyboard: trigger is a real button, Space/Enter activates.
 */
export default function InfoTooltip({ label, children }: InfoTooltipProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const tooltipId = useId();

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span
      className="info-tooltip"
      ref={rootRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="info-tooltip__trigger"
        aria-label={label}
        aria-describedby={open ? tooltipId : undefined}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        i
      </button>
      {open && (
        <span role="tooltip" id={tooltipId} className="info-tooltip__bubble">
          {children}
        </span>
      )}
    </span>
  );
}
